import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChangeTypeDto } from 'src/api/FiretrolApiClient';
import { useChangeTypes } from 'src/api/queries/changeTypes/useChangeTypes';
import { useDeleteChangeType } from 'src/api/queries/changeTypes/useChangeTypeMutations';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';

const getColumns = (): MRT_ColumnDef<ChangeTypeDto>[] => {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'sortOrder',
      header: 'Sort Order',
    },
    {
      id: 'isActive',
      header: 'Status',
      accessorFn: (row) => (row.isActive ? 'Active' : 'Inactive'),
      Cell: ({ cell }) => {
        const active = cell.getValue<string>() === 'Active';
        return (
          <Badge color={active ? 'green' : 'gray'}>
            {active ? 'Active' : 'Inactive'}
          </Badge>
        );
      },
    },
  ];
};

export const ChangeTypes: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching, isError } = useChangeTypes();
  const changeTypes = React.useMemo(() => data ?? [], [data]);
  const deleteMutation = useDeleteChangeType();

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'change-types',
    columns: columns,
    defaultSorting: [{ id: 'sortOrder', desc: false }],
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 150 } },
      renderRowActions: ({ row }) => {
        const changeType = row.original;
        return (
          <Group>
            <EditActionIcon
              onClick={() =>
                navigate(`/settings/change-types/edit/${changeType.id}`)
              }
            />
            <DeleteActionIcon
              label="Delete Change Type"
              requiredConfirmation={true}
              confirmationText={`Are you sure you want to delete "${changeType.name}"? Types already used by a change can only be deactivated.`}
              onDelete={async () => {
                await deleteMutation.mutateAsync(changeType.id);
              }}
            />
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: changeTypes,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton
            onClick={() => navigate('/settings/change-types/create')}
          >
            Add Change Type
          </CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: changeTypes.length,
    isLoading,
    isFetching,
    isError,
  });

  return (
    <>
      <Header
        title="Change Types"
        breadcrumbProps={{
          items: [{ text: 'Settings' }, { text: 'Change Types' }],
        }}
        helpText="Change types categorize changes (e.g., Owner Change, Field Change). Inactive types stay on historic changes but are hidden from the picker."
      />
      {table}
    </>
  );
};
